import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Loader2, ArrowLeft, CheckCircle2, AlertTriangle, XCircle,
  ChevronDown, ChevronUp, Minus, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'variances', label: 'Variances' },
  { key: 'not_counted', label: 'Not Counted' },
  { key: 'match', label: 'Match' },
];

const STATUS_CONFIG = {
  match: { label: 'Match', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
  over: { label: 'Over', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  under: { label: 'Under', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  not_counted: { label: 'Not Counted', color: 'bg-slate-100 text-slate-500 border-slate-200', icon: Minus },
  no_expected: { label: 'No Expected', color: 'bg-blue-100 text-blue-600 border-blue-200', icon: Minus },
};

function VarianceBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.no_expected;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`${cfg.color} text-xs gap-1`} data-testid={`variance-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
}

function ReconciliationRow({ line }) {
  const [expanded, setExpanded] = useState(false);
  const hasNotes = !!line.notes;

  return (
    <div
      className={`border-b border-slate-100 last:border-b-0 ${
        line.status === 'over' || line.status === 'under' ? 'bg-amber-50/30' : ''
      }`}
      data-testid={`recon-row-${line.line_id}`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{line.product_name}</p>
          {line.location && <p className="text-xs text-slate-400 truncate">{line.location}</p>}
        </div>
        <div className="flex items-center gap-3 shrink-0 text-right">
          <div className="text-center min-w-[50px]">
            <p className="text-xs text-slate-400">Counted</p>
            <p className="text-sm font-semibold text-slate-800" data-testid={`recon-counted-${line.line_id}`}>
              {line.counted_qty != null ? line.counted_qty : '--'}
            </p>
          </div>
          <div className="text-center min-w-[50px]">
            <p className="text-xs text-slate-400">Expected</p>
            <p className="text-sm font-medium text-slate-500" data-testid={`recon-expected-${line.line_id}`}>
              {line.expected_qty != null ? line.expected_qty : '--'}
            </p>
          </div>
          <div className="text-center min-w-[50px]">
            <p className="text-xs text-slate-400">Variance</p>
            <p className={`text-sm font-semibold ${
              line.variance_qty > 0 ? 'text-red-600' : line.variance_qty < 0 ? 'text-amber-600' : 'text-slate-500'
            }`} data-testid={`recon-variance-${line.line_id}`}>
              {line.variance_qty != null ? (line.variance_qty > 0 ? `+${line.variance_qty}` : line.variance_qty) : '--'}
            </p>
          </div>
          <VarianceBadge status={line.status} />
          {hasNotes && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded transition-colors text-slate-300 hover:text-slate-500"
              data-testid={`recon-expand-${line.line_id}`}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>
      {expanded && hasNotes && (
        <div className="px-4 pb-3">
          <div className="text-xs text-slate-500 bg-slate-50 rounded px-2 py-1.5">
            <span className="font-medium">Auditor note:</span> {line.notes}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReconciliationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionId = id ? parseInt(id, 10) : null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const filterParam = filter !== 'all' ? `?variance_filter=${filter}` : '';
      const res = await api.get(`/reconciliation/sessions/${sessionId}/reconciliation${filterParam}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  }, [sessionId, filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredLines = useMemo(() => {
    if (!data?.lines) return [];
    let result = data.lines;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        (l.product_name || '').toLowerCase().includes(q) ||
        (l.location || '').toLowerCase().includes(q)
      );
    }
    // Sort alphabetically by product name
    result = [...result].sort((a, b) =>
      (a.product_name || '').localeCompare(b.product_name || '')
    );
    return result;
  }, [data?.lines, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const unmatched = data?.unmatched_items || [];

  return (
    <div className="pb-4" data-testid="reconciliation-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate(`/count/sessions/${sessionId}`)}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
            data-testid="recon-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-slate-900" data-testid="recon-title">Reconciliation</h1>
        </div>

        {/* Summary */}
        <div className="flex gap-2 flex-wrap mb-3">
          <Badge variant="outline" className="text-xs bg-slate-50" data-testid="recon-badge-total">{summary.total} total</Badge>
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200" data-testid="recon-badge-match">{summary.match} match</Badge>
          <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200" data-testid="recon-badge-over">{summary.over || 0} over</Badge>
          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200" data-testid="recon-badge-under">{summary.under || 0} under</Badge>
          <Badge variant="outline" className="text-xs bg-slate-100 text-slate-500" data-testid="recon-badge-uncounted">{summary.not_counted} uncounted</Badge>
        </div>

        {/* Filters */}
        <div className="flex gap-1.5 mb-2">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filter === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
              data-testid={`recon-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="pl-9 h-9 text-sm"
            data-testid="recon-search"
          />
        </div>
      </div>

      {/* Lines */}
      <Card className="mx-4 mt-3 overflow-hidden" data-testid="recon-lines-card">
        {filteredLines.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm" data-testid="recon-empty">
            No lines match the current filter
          </div>
        ) : (
          filteredLines.map(line => (
            <ReconciliationRow key={line.line_id} line={line} />
          ))
        )}
      </Card>

      {/* Unmatched items */}
      {unmatched.length > 0 && (
        <div className="mx-4 mt-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            <XCircle className="w-4 h-4 text-red-400" />
            Unmatched Items ({unmatched.length})
          </h2>
          <Card className="overflow-hidden" data-testid="recon-unmatched-card">
            {unmatched.map(um => (
              <div key={um.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 truncate">{um.product_name}</p>
                  <p className="text-xs text-slate-400">{um.item_type} &middot; {um.unit_label}</p>
                </div>
                <span className="text-sm font-medium text-slate-600">{um.quantity ?? '--'}</span>
                <Badge variant="outline" className={`text-xs ${
                  um.status === 'resolved' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  {um.status}
                </Badge>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
